import type {
  AggregateRating,
  Graph,
  MerchantReturnPolicy,
  Offer,
  OfferShippingDetails,
  Organization,
  Product,
  ProductGroup,
  Review,
} from 'schema-dts';

import type {
  MerchantReturnPolicyInput,
  OrganizationSchemaInput,
  ProductGroupSchemaInput,
  ProductSchemaInput,
} from './types';

/**
 * Format price according to schema.org requirements
 */
export const formatSchemaPrice = (price: number): string => {
  return price.toFixed(2);
};

/**
 * Format availability status for schema.org
 */
export const formatAvailability = (availability: ProductSchemaInput['availability']): string => {
  const availabilityMap = {
    InStock: 'https://schema.org/InStock',
    OutOfStock: 'https://schema.org/OutOfStock',
    PreOrder: 'https://schema.org/PreOrder',
    LimitedAvailability: 'https://schema.org/LimitedAvailability',
  };

  return availabilityMap[availability];
};

/**
 * Format condition for schema.org
 */
export const formatCondition = (condition?: ProductSchemaInput['condition']): string => {
  if (!condition) return 'https://schema.org/NewCondition';

  const conditionMap = {
    NewCondition: 'https://schema.org/NewCondition',
    UsedCondition: 'https://schema.org/UsedCondition',
    RefurbishedCondition: 'https://schema.org/RefurbishedCondition',
    DamagedCondition: 'https://schema.org/DamagedCondition',
  };

  return conditionMap[condition];
};

/**
 * Create Organization schema
 */
export const createOrganizationSchema = (input: OrganizationSchemaInput): Organization => {
  const org: Organization = {
    '@type': 'Organization',
    name: input.name,
  };

  if (input.url) org.url = input.url;
  if (input.logo) org.logo = input.logo;
  if (input.sameAs) org.sameAs = input.sameAs;

  if (input.contactPoint) {
    org.contactPoint = {
      '@type': 'ContactPoint',
      ...input.contactPoint,
    };
  }

  return org;
};

/**
 * Create MerchantReturnPolicy schema
 */
export const createReturnPolicySchema = (
  input: MerchantReturnPolicyInput
): MerchantReturnPolicy => {
  const policy: MerchantReturnPolicy = {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: input.applicableCountry,
    returnPolicyCategory: `https://schema.org/${input.returnPolicyCategory}`,
  };

  if (input.returnDays) {
    policy.merchantReturnDays = input.returnDays;
  }

  if (input.returnMethod) {
    policy.returnMethod = `https://schema.org/${input.returnMethod}`;
  }

  if (input.returnFees) {
    policy.returnFees = `https://schema.org/${input.returnFees}`;
  }

  if (input.refundType) {
    policy.refundType = `https://schema.org/${input.refundType}`;
  }

  return policy;
};

/**
 * Create shipping details schema
 */
export const createShippingDetailsSchema = (
  input: NonNullable<ProductSchemaInput['shippingDetails']>
): OfferShippingDetails => {
  const shippingDetails: OfferShippingDetails = {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: formatSchemaPrice(input.shippingRate),
      currency: input.shippingCurrency,
    },
    shippingDestination: input.shippingDestination.map((country) => ({
      '@type': 'DefinedRegion' as const,
      addressCountry: country,
    })),
  };

  if (input.deliveryTime) {
    shippingDetails.deliveryTime = {
      '@type': 'ShippingDeliveryTime',
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: input.deliveryTime.minDays,
        maxValue: input.deliveryTime.maxDays,
        unitCode: 'DAY',
      },
    };
  }

  return shippingDetails;
};

/**
 * Create aggregate rating schema
 */
export const createAggregateRatingSchema = (
  input: NonNullable<ProductSchemaInput['aggregateRating']>
): AggregateRating => {
  return {
    '@type': 'AggregateRating',
    ratingValue: input.ratingValue,
    reviewCount: input.reviewCount,
    bestRating: input.bestRating || 5,
    worstRating: input.worstRating || 1,
  };
};

/**
 * Create review schema
 */
export const createReviewSchema = (
  review: NonNullable<ProductSchemaInput['reviews']>[number],
  productId: string
): Review => {
  const reviewSchema: Review = {
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Product',
      '@id': productId,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.reviewRating,
      bestRating: 5,
      worstRating: 1,
    },
    author: {
      '@type': 'Person',
      name: review.author,
    },
  };

  if (review.reviewBody) {
    reviewSchema.reviewBody = review.reviewBody;
  }

  if (review.datePublished) {
    reviewSchema.datePublished = review.datePublished;
  }

  return reviewSchema;
};

/**
 * Create offer schema
 */
export const createOfferSchema = (input: ProductSchemaInput): Offer => {
  const offer: Offer = {
    '@type': 'Offer',
    price: formatSchemaPrice(input.price),
    priceCurrency: input.priceCurrency,
    availability: formatAvailability(input.availability) as Offer['availability'],
    itemCondition: formatCondition(input.condition) as Offer['itemCondition'],
  };

  if (input.url) {
    offer.url = input.url;
  }

  if (input.priceValidUntil) {
    offer.priceValidUntil = input.priceValidUntil;
  }

  if (input.seller) {
    offer.seller =
      typeof input.seller === 'string'
        ? { '@type': 'Organization' as const, name: input.seller }
        : { '@type': 'Organization' as const, ...input.seller };
  }

  if (input.shippingDetails) {
    offer.shippingDetails = createShippingDetailsSchema(input.shippingDetails);
  }

  return offer;
};

/**
 * Create product schema
 */
export const createProductSchema = (input: ProductSchemaInput): Product => {
  const productId = `#product-${input.id}`;

  const product: Product = {
    '@type': 'Product',
    '@id': productId,
    name: input.name,
    description: input.description,
    image: Array.isArray(input.image) ? input.image : [input.image],
    offers: createOfferSchema(input),
  };

  // Add identifiers
  if (input.sku) product.sku = input.sku;
  if (input.gtin) product.gtin = input.gtin;
  if (input.mpn) product.mpn = input.mpn;

  // Add brand
  if (input.brand) {
    product.brand =
      typeof input.brand === 'string'
        ? { '@type': 'Brand' as const, name: input.brand }
        : { '@type': 'Brand' as const, ...input.brand };
  }

  // Add aggregate rating
  if (input.aggregateRating) {
    product.aggregateRating = createAggregateRatingSchema(input.aggregateRating);
  }

  // Add dimensions
  if (input.weight) {
    product.weight = {
      '@type': 'QuantitativeValue',
      value: input.weight.value,
      unitCode: input.weight.unitCode,
    };
  }

  if (input.width) {
    product.width = {
      '@type': 'QuantitativeValue',
      value: input.width.value,
      unitCode: input.width.unitCode,
    };
  }

  if (input.height) {
    product.height = {
      '@type': 'QuantitativeValue',
      value: input.height.value,
      unitCode: input.height.unitCode,
    };
  }

  if (input.depth) {
    product.depth = {
      '@type': 'QuantitativeValue',
      value: input.depth.value,
      unitCode: input.depth.unitCode,
    };
  }

  // Add variant information
  if (input.productGroupId) {
    product.inProductGroupWithID = input.productGroupId;
  }

  // Add variant-specific attributes
  if (input.variantAttributes) {
    // Return a new object with variant attributes to avoid type errors
    return {
      ...product,
      ...input.variantAttributes,
    } as Product;
  }

  return product;
};

/**
 * Create product group schema with variants
 */
export const createProductGroupSchema = (input: ProductGroupSchemaInput) => {
  const productGroup: ProductGroup & { '@context': string; '@id': string; productGroupID: string } =
    {
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      '@id': `#product-group-${input.id}`,
      productGroupID: input.id,
      name: input.name,
      variesBy: input.variesBy,
    };

  if (input.description) {
    productGroup.description = input.description;
  }

  if (input.brand) {
    productGroup.brand =
      typeof input.brand === 'string'
        ? { '@type': 'Brand', name: input.brand }
        : { '@type': 'Brand', ...input.brand };
  }

  // Create variants
  const variants = input.variants.map((variant) => ({
    ...createProductSchema({
      ...variant,
      productGroupId: `#product-group-${input.id}`,
    }),
    '@context': undefined, // Remove context from individual variants
  }));

  productGroup.hasVariant = variants;

  // Also create reviews array if any variant has reviews
  const allReviews = input.variants.flatMap(
    (variant) =>
      variant.reviews?.map((review) => createReviewSchema(review, `#product-${variant.id}`)) || []
  );

  if (allReviews.length > 0) {
    return {
      '@context': 'https://schema.org',
      '@graph': [productGroup, ...allReviews],
    };
  }

  return productGroup;
};

/**
 * Create complete product page schema with reviews
 */
export const createProductPageSchema = (
  product: ProductSchemaInput,
  organization?: OrganizationSchemaInput,
  returnPolicy?: MerchantReturnPolicyInput
): Graph => {
  const productSchema = createProductSchema(product);
  const graph: (Product | Review | Organization)[] = [productSchema];

  // Add reviews
  if (product.reviews) {
    const reviews = product.reviews.map((review) =>
      createReviewSchema(review, productSchema['@id']!)
    );
    graph.push(...reviews);
  }

  // Add organization with return policy
  if (organization || returnPolicy) {
    const orgSchema: Organization = {
      '@type': 'Organization',
      name: organization?.name || 'Organization',
      ...(organization?.url && { url: organization.url }),
      ...(organization?.logo && { logo: organization.logo }),
      ...(organization?.sameAs && { sameAs: organization.sameAs }),
    };

    // Add contact point
    if (organization?.contactPoint) {
      orgSchema.contactPoint = {
        '@type': 'ContactPoint',
        ...organization.contactPoint,
      };
    }

    // Add return policy as hasMerchantReturnPolicy
    if (returnPolicy) {
      orgSchema.hasMerchantReturnPolicy = createReturnPolicySchema(returnPolicy);
    }

    graph.push(orgSchema);
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};
